import { useMap } from "@vis.gl/react-maplibre";
import type { MapMouseEvent, PointLike } from "maplibre-gl";
import { useEffect, type MutableRefObject } from "react";
import useEvent from "react-use-event-hook";

import { getAliveMap } from "./maplibre-map.utils";
import {
  useMapLibreEndPlaceInfoOnToolPick,
  useMapLibreIsPickingPlace,
} from "./maplibre-place-info.context";
import {
  POLYGON_EDITOR_FILL_LAYER_ID,
  POLYGON_EDITOR_HANDLE_GRAB_PADDING_PX,
  POLYGON_EDITOR_LONG_PRESS_MOVE_TOLERANCE_PX,
  POLYGON_EDITOR_LONG_PRESS_MS,
  POLYGON_EDITOR_MIDPOINT_LAYER_ID,
  POLYGON_EDITOR_SNAP_DISTANCE_PX,
  POLYGON_EDITOR_VERTEX_LAYER_ID,
  POLYGON_EDITOR_VERTEX_RADIUS_PX,
} from "./maplibre-polygon-editor.constants";
import type {
  IEditablePolygon,
  IPolygonVertexRef,
  TEditorDrag,
  TPolygonEditorMode,
  TPolygonPosition,
} from "./maplibre-polygon-editor.models";
import {
  doesVertexMoveSelfIntersect,
  toVertexRef,
  translatePolygonRings,
  withInsertedVertex,
  withMovedVertex,
  withRemovedVertex,
} from "./maplibre-polygon-editor.utils";

interface IUseMapLibrePolygonEditorPointerArgs {
  mode: TPolygonEditorMode;
  polygonsRef: MutableRefObject<IEditablePolygon[]>;
  commit: (polygons: IEditablePolygon[]) => void;
}

/**
 * Every pointer gesture the Leaflet-geoman editors offered, on a MapLibre map.
 *
 * Handles are GPU circles rather than DOM markers, so they are hit-tested with `queryRenderedFeatures`
 * instead of by their own listeners. That is what keeps a boundary with a few thousand corners editable:
 * geoman put an HTML element on each one.
 */
export const useMapLibrePolygonEditorPointer = ({
  mode,
  polygonsRef,
  commit,
}: IUseMapLibrePolygonEditorPointerArgs): void => {
  const { current: mapRef } = useMap();
  const isPickingPlace = useMapLibreIsPickingPlace();
  // Keyed on the mode, not on "any tool": switching from reshape to drag is a new pick too.
  useMapLibreEndPlaceInfoOnToolPick(mode === "none" ? undefined : mode);

  const applyVertexPosition = useEvent(
    (vertex: IPolygonVertexRef, position: TPolygonPosition): void => {
      const polygon = polygonsRef.current.find(
        ({ id }) => id === vertex.polygonId,
      );
      const ring = polygon?.rings[vertex.ringIndex];

      if (!ring) {
        return;
      }

      // Geoman held the vertex at its last valid spot rather than letting a shape cross itself; it picks
      // the drag back up as soon as the pointer returns somewhere legal.
      if (
        doesVertexMoveSelfIntersect({
          ring,
          vertexIndex: vertex.vertexIndex,
          position,
        })
      ) {
        return;
      }

      commit(withMovedVertex(polygonsRef.current, vertex, position));
    },
  );

  useEffect(() => {
    const map = getAliveMap(mapRef);

    // Place info owns the pointer meanwhile: picking up a midpoint would insert a corner on mousedown.
    if (!map || isPickingPlace) {
      return;
    }

    const isReshaping = mode === "edit";
    const isDraggingShapes = mode === "drag";

    if (!isReshaping && !isDraggingShapes && mode !== "remove") {
      return;
    }

    let drag: TEditorDrag | null = null;
    let pressTimer: ReturnType<typeof setTimeout> | null = null;
    let pressStart: { x: number; y: number } | null = null;

    const canvas = map.getCanvas();

    const queryLayer = (
      point: PointLike | [PointLike, PointLike],
      layerIds: string[],
    ): ReturnType<typeof map.queryRenderedFeatures> => {
      const layers = layerIds.filter(id => map.getLayer(id));

      return layers.length ? map.queryRenderedFeatures(point, { layers }) : [];
    };

    const queryHandles = ({
      x,
      y,
    }: {
      x: number;
      y: number;
    }): ReturnType<typeof map.queryRenderedFeatures> => {
      const pad =
        POLYGON_EDITOR_VERTEX_RADIUS_PX + POLYGON_EDITOR_HANDLE_GRAB_PADDING_PX;

      return queryLayer(
        [
          [x - pad, y - pad],
          [x + pad, y + pad],
        ],
        [POLYGON_EDITOR_VERTEX_LAYER_ID, POLYGON_EDITOR_MIDPOINT_LAYER_ID],
      );
    };

    /**
     * Magnetises a dragged corner onto any other corner within snapping distance, as geoman's snappable
     * option did. Every corner is a candidate except the one in hand.
     */
    const resolveSnappedPosition = (
      event: MapMouseEvent,
      moving: IPolygonVertexRef,
    ): TPolygonPosition => {
      const pointer = event.point;
      let closest: { position: TPolygonPosition; distance: number } | null =
        null;

      for (const { id, rings } of polygonsRef.current) {
        for (const [ringIndex, ring] of rings.entries()) {
          for (const [vertexIndex, position] of ring.entries()) {
            const isSelf =
              id === moving.polygonId &&
              ringIndex === moving.ringIndex &&
              vertexIndex === moving.vertexIndex;

            if (isSelf) {
              continue;
            }

            const projected = map.project(position);
            const distance = Math.hypot(
              projected.x - pointer.x,
              projected.y - pointer.y,
            );

            if (
              distance <= POLYGON_EDITOR_SNAP_DISTANCE_PX &&
              (!closest || distance < closest.distance)
            ) {
              closest = { position, distance };
            }
          }
        }
      }

      return closest ? closest.position : [event.lngLat.lng, event.lngLat.lat];
    };

    const resolveHoverCursor = (event: MapMouseEvent): string => {
      if (queryHandles(event.point).length) {
        return "move";
      }

      return queryLayer(event.point, [POLYGON_EDITOR_FILL_LAYER_ID]).length
        ? "grab"
        : "";
    };

    const clearPressTimer = (): void => {
      if (pressTimer) {
        clearTimeout(pressTimer);
        pressTimer = null;
      }
    };

    const beginShapeDrag = (
      polygonId: string,
      anchor: TPolygonPosition,
    ): void => {
      const polygon = polygonsRef.current.find(({ id }) => id === polygonId);

      if (!polygon) {
        return;
      }

      drag = { kind: "shape", polygonId, anchor, baseline: polygon.rings };
      // The map must not pan underneath a shape that is being carried.
      map.dragPan.disable();
      canvas.style.cursor = "grabbing";
    };

    const endDrag = (): void => {
      if (drag?.kind === "shape") {
        map.dragPan.enable();
      }

      drag = null;
      canvas.style.cursor = "";
    };

    const handleMouseDown = (event: MapMouseEvent): void => {
      if (drag) {
        return;
      }

      if (isReshaping) {
        const [handle] = queryHandles(event.point);
        const vertex = toVertexRef(handle?.properties ?? null);

        if (handle && vertex) {
          // Stops MapLibre panning the map for the rest of this gesture.
          event.preventDefault();

          if (handle.layer.id === POLYGON_EDITOR_MIDPOINT_LAYER_ID) {
            // A midpoint becomes a real corner the moment it is picked up — geoman's middle markers.
            commit(
              withInsertedVertex(polygonsRef.current, vertex, [
                event.lngLat.lng,
                event.lngLat.lat,
              ]),
            );
          }

          drag = { kind: "vertex", vertex };
          canvas.style.cursor = "move";

          return;
        }
      }

      const [shape] = queryLayer(event.point, [POLYGON_EDITOR_FILL_LAYER_ID]);
      const polygonId = shape?.properties?.polygonId;

      if (typeof polygonId !== "string") {
        return;
      }

      const anchor: TPolygonPosition = [event.lngLat.lng, event.lngLat.lat];

      if (isDraggingShapes) {
        event.preventDefault();
        beginShapeDrag(polygonId, anchor);

        return;
      }

      // Reshape mode: a press held roughly still picks the whole shape up instead. The map keeps its
      // default drag until the timer fires, so a quick press still pans.
      pressStart = { x: event.point.x, y: event.point.y };
      clearPressTimer();
      pressTimer = setTimeout(
        () => beginShapeDrag(polygonId, anchor),
        POLYGON_EDITOR_LONG_PRESS_MS,
      );
    };

    const handleMouseMove = (event: MapMouseEvent): void => {
      if (
        pressTimer &&
        pressStart &&
        Math.hypot(event.point.x - pressStart.x, event.point.y - pressStart.y) >
          POLYGON_EDITOR_LONG_PRESS_MOVE_TOLERANCE_PX
      ) {
        clearPressTimer();
      }

      if (drag?.kind === "vertex") {
        applyVertexPosition(
          drag.vertex,
          resolveSnappedPosition(event, drag.vertex),
        );

        return;
      }

      if (drag?.kind === "shape") {
        const { polygonId, anchor, baseline } = drag;
        const moved = translatePolygonRings(
          baseline,
          event.lngLat.lng - anchor[0],
          event.lngLat.lat - anchor[1],
        );

        commit(
          polygonsRef.current.map(polygon =>
            polygon.id === polygonId ? { ...polygon, rings: moved } : polygon,
          ),
        );

        return;
      }

      if (!isReshaping) {
        return;
      }

      /*
       * The only cue left that these are grabbable, now that handles are painted rather than DOM elements
       * carrying their own cursor. Geoman put `move` on a draggable vertex, and the areas editor put `grab`
       * on the body of the shape it was reshaping — because a long press there picks the whole thing up.
       */
      canvas.style.cursor = resolveHoverCursor(event);
    };

    // On the window, so releasing the button off the map still ends the gesture.
    const handlePointerUp = (): void => {
      clearPressTimer();
      pressStart = null;

      if (drag) {
        endDrag();
      }
    };

    const handleVertexContextMenu = (event: MapMouseEvent): void => {
      const [handle] = queryLayer(event.point, [
        POLYGON_EDITOR_VERTEX_LAYER_ID,
      ]);
      const vertex = toVertexRef(handle?.properties ?? null);

      if (!vertex) {
        return;
      }

      event.originalEvent.preventDefault();
      commit(withRemovedVertex(polygonsRef.current, vertex));
    };

    const handleRemoveClick = (event: MapMouseEvent): void => {
      const [shape] = queryLayer(event.point, [POLYGON_EDITOR_FILL_LAYER_ID]);
      const polygonId = shape?.properties?.polygonId;

      if (typeof polygonId !== "string") {
        return;
      }

      commit(polygonsRef.current.filter(({ id }) => id !== polygonId));
    };

    if (mode === "remove") {
      map.on("click", handleRemoveClick);

      return () => {
        getAliveMap(mapRef)?.off("click", handleRemoveClick);
      };
    }

    map.on("mousedown", handleMouseDown);
    map.on("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handlePointerUp);

    if (isReshaping) {
      map.on("contextmenu", handleVertexContextMenu);
    }

    return () => {
      clearPressTimer();
      window.removeEventListener("mouseup", handlePointerUp);
      const aliveMap = getAliveMap(mapRef);
      if (!aliveMap) {
        return;
      }

      aliveMap.off("mousedown", handleMouseDown);
      aliveMap.off("mousemove", handleMouseMove);
      aliveMap.off("contextmenu", handleVertexContextMenu);
      // Never leave the map frozen or the cursor stuck if this unmounts mid-drag.
      aliveMap.dragPan.enable();
      canvas.style.cursor = "";
    };
  }, [mapRef, mode, isPickingPlace, polygonsRef, commit, applyVertexPosition]);
};
